/**
 * Background layer of the breadcrumb bar (sections/breadcrumbs.liquid). It marks itself
 * `covered` once the bar has scrolled fully under the sticky header, so the section's CSS
 * can move the bar's drop shadow onto the header while the trail is out of sight.
 */
class BreadcrumbsBackground extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {ResizeObserver | null} */
  #headerResizeObserver = null;

  connectedCallback() {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return;

    // The covered line is the sticky header's bottom edge, so follow its height across breakpoints.
    this.#headerResizeObserver = new ResizeObserver(() => this.#observeBelow(header.offsetHeight));
    this.#headerResizeObserver.observe(header);
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#headerResizeObserver?.disconnect();
    this.removeAttribute('covered');
  }

  /**
   * Watches the bar against the viewport minus the header's height: once no part of it is
   * below the header, and it left upwards (not below the fold), it counts as covered.
   * @param {number} headerHeight - The sticky header's height in pixels.
   */
  #observeBelow(headerHeight) {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        const rootTop = entry.rootBounds?.top ?? headerHeight;
        this.toggleAttribute('covered', !entry.isIntersecting && entry.boundingClientRect.top < rootTop);
      },
      { rootMargin: `-${headerHeight}px 0px 0px 0px` }
    );
    this.#intersectionObserver.observe(this);
  }
}

if (!customElements.get('breadcrumbs-background')) {
  customElements.define('breadcrumbs-background', BreadcrumbsBackground);
}
